import { create } from 'zustand'
import {
  LEGACY_RFI_ITEMS,
  RfiItem,
  rfiStepsFor,
  type RfiRequest,
  type RfiStep,
} from './rfiRequest'
import {
  readRfiDraft,
  removeRfiDrafts,
  writeRfiDraft,
  type RfiDraft,
} from './rfiDraftStorage'

export type { RfiStep }
export { getNextStep, getPreviousStep } from './rfiRequest'

type RfiDocumentState = { name: string; s3Url: string } | null
type RfiDocumentKey = 'proofOfAddress' | 'certificate'

export type RfiBankLinkMethod = 'plaid' | 'manual'

type RfiApplicationStoreData = {
  currentStep: RfiStep
  items: RfiItem[]
  note: string | null
  explainer: string
  acceptedTerms: boolean
  identityVerificationComplete: boolean
  documents: Record<RfiDocumentKey, RfiDocumentState>
  bankLinked: RfiBankLinkMethod | null
  manualBankDocumentS3Urls: string[]
  manualBankSummary: { bankName: string; last4: string } | null
  /**
   * Whose draft this is (`rfiDraftKey`) and for which request round
   * (`rfiRequestKey`), set by `resume` and kept through `completeSubmission`.
   * A draft is only ever resumed by the same user, in the same organization,
   * for the same request, and a submitted round stays submitted.
   */
  draftKey: string | null
  requestKey: string | null
}

type RfiApplicationStoreActions = {
  resume: (draftKey: string, request: RfiRequest, requestKey: string) => void
  completeSubmission: () => void
  setRequest: (request: RfiRequest) => void
  setCurrentStep: (step: RfiStep) => void
  setAcceptedTerms: (accepted: boolean) => void
  setExplainer: (explainer: string) => void
  setIdentityVerificationComplete: (complete: boolean) => void
  setDocument: (key: RfiDocumentKey, doc: RfiDocumentState) => void
  setBankLinked: (
    method: RfiBankLinkMethod,
    manual?: {
      documentS3Urls: string[]
      summary: { bankName: string; last4: string }
    },
  ) => void
  isStepComplete: (step: RfiStep) => boolean
  isSubmitReady: () => boolean
  collectS3Urls: () => string[]
  explainerToSend: () => string | undefined
  reset: () => void
}

type RfiApplicationStore = RfiApplicationStoreData & RfiApplicationStoreActions

const DOCUMENT_KEY_BY_ITEM: Partial<Record<RfiItem, RfiDocumentKey>> = {
  [RfiItem.ProofOfAddress]: 'proofOfAddress',
  [RfiItem.CertificateOfIncorporation]: 'certificate',
}

export function documentKeyFor(item: RfiItem): RfiDocumentKey | undefined {
  return DOCUMENT_KEY_BY_ITEM[item]
}

function getInitialState(): RfiApplicationStoreData {
  return {
    currentStep: 'get-started',
    items: [...LEGACY_RFI_ITEMS],
    note: null,
    explainer: '',
    acceptedTerms: false,
    identityVerificationComplete: false,
    documents: {
      proofOfAddress: null,
      certificate: null,
    },
    bankLinked: null,
    manualBankDocumentS3Urls: [],
    manualBankSummary: null,
    draftKey: null,
    requestKey: null,
  }
}

type DraftFields = Pick<
  RfiApplicationStoreData,
  'explainer' | 'documents' | 'bankLinked' | 'identityVerificationComplete'
>

function hasStorableProgress(
  state: Omit<DraftFields, 'identityVerificationComplete'>,
) {
  return (
    state.explainer.trim().length > 0 ||
    Object.values(state.documents).some((doc) => doc !== null) ||
    state.bankLinked !== null
  )
}

export function hasRfiProgress(state: DraftFields): boolean {
  return hasStorableProgress(state) || state.identityVerificationComplete
}

function toDraft(state: RfiApplicationStoreData, requestKey: string): RfiDraft {
  return {
    request: requestKey,
    currentStep:
      state.currentStep === 'submitted' ? 'get-started' : state.currentStep,
    acceptedTerms: state.acceptedTerms,
    explainer: state.explainer,
    documents: state.documents,
    bankLinked: state.bankLinked,
    manualBankDocumentS3Urls: state.manualBankDocumentS3Urls,
    manualBankSummary: state.manualBankSummary,
  }
}

export const useRfiApplicationStore = create<RfiApplicationStore>(
  (set, get) => ({
    ...getInitialState(),

    resume: (draftKey, { items, note }, requestKey) => {
      const current = get()
      if (current.draftKey === draftKey && current.requestKey === requestKey) {
        return
      }
      const stored = readRfiDraft(draftKey)
      const draft = stored?.request === requestKey ? stored : null
      set({
        ...getInitialState(),
        ...(draft && {
          acceptedTerms: draft.acceptedTerms,
          explainer: draft.explainer,
          documents: draft.documents,
          bankLinked: draft.bankLinked,
          manualBankDocumentS3Urls: draft.manualBankDocumentS3Urls,
          manualBankSummary: draft.manualBankSummary,
        }),
        items: [...items],
        note,
        draftKey,
        requestKey,
      })
      if (!draft) return
      const steps = rfiStepsFor(items, note)
      const target = steps.indexOf(draft.currentStep)
      const { isStepComplete } = get()
      const firstOpen = steps.findIndex((step) => !isStepComplete(step))
      const index =
        firstOpen >= 0 && firstOpen < target ? firstOpen : Math.max(target, 0)
      set({ currentStep: steps[index] })
    },

    completeSubmission: () => {
      const { items, note, draftKey, requestKey } = get()
      set({
        ...getInitialState(),
        items,
        note,
        draftKey,
        requestKey,
        currentStep: 'submitted',
      })
      removeRfiDrafts()
    },

    setRequest: ({ items, note }) => set({ items: [...items], note }),

    setCurrentStep: (step) => set({ currentStep: step }),

    setAcceptedTerms: (accepted) => set({ acceptedTerms: accepted }),

    setExplainer: (explainer) => set({ explainer }),

    setIdentityVerificationComplete: (complete) =>
      set({ identityVerificationComplete: complete }),

    setDocument: (key, doc) =>
      set((state) => ({
        documents: { ...state.documents, [key]: doc },
      })),

    setBankLinked: (method, manual) =>
      set({
        bankLinked: method,
        manualBankDocumentS3Urls: manual?.documentS3Urls ?? [],
        manualBankSummary: manual?.summary ?? null,
      }),

    isStepComplete: (step) => {
      const state = get()
      switch (step) {
        case 'additional-info':
          return state.note === null || state.explainer.trim().length > 0
        case 'identity':
          return state.identityVerificationComplete
        case 'documents':
          return state.items.every((item) => {
            const key = documentKeyFor(item)
            return key === undefined || state.documents[key] !== null
          })
        case 'bank-account':
          return state.bankLinked !== null
        default:
          return true
      }
    },

    isSubmitReady: () => {
      const { items, isStepComplete } = get()
      return (
        isStepComplete('additional-info') &&
        (!items.includes(RfiItem.Identity) || isStepComplete('identity')) &&
        isStepComplete('documents') &&
        (!items.includes(RfiItem.PrimaryBankAccount) ||
          isStepComplete('bank-account'))
      )
    },

    collectS3Urls: () => {
      const state = get()
      const requested = state.items.flatMap((item) => {
        const key = documentKeyFor(item)
        const doc = key ? state.documents[key] : null
        return doc ? [doc.s3Url] : []
      })
      const manual = state.items.includes(RfiItem.PrimaryBankAccount)
        ? state.manualBankDocumentS3Urls
        : []
      return [...requested, ...manual]
    },

    explainerToSend: () => {
      const { note, explainer } = get()
      return note ? explainer.trim() || undefined : undefined
    },

    reset: () => set(getInitialState()),
  }),
)

useRfiApplicationStore.subscribe((state) => {
  if (
    !state.draftKey ||
    !state.requestKey ||
    state.currentStep === 'submitted'
  ) {
    return
  }
  if (hasStorableProgress(state)) {
    writeRfiDraft(state.draftKey, toDraft(state, state.requestKey))
  } else {
    removeRfiDrafts()
  }
})

export function discardRfiDrafts(): void {
  useRfiApplicationStore.getState().reset()
  removeRfiDrafts()
}
